import type { ColorPartial } from "@mui/material/styles/createPalette";
import createTheme from "@mui/material/styles/createTheme";
import type { Shadows } from "@mui/material/styles/shadows";

import sunoBreakpoints from "app/themes/breakpoints";
import { error, misc, neutral, primary, success } from "app/themes/colors";
import { getSunoComponents } from "app/themes/components";
import sunoShadows from "app/themes/shadows";

let sunoTheme = createTheme();

sunoTheme = createTheme(sunoTheme, {
  breakpoints: sunoBreakpoints,
  palette: {
    neutral,
    primary: {
      main: primary[400],
      dark: primary[700],
      light: primary[50]
    },
    success: {
      main: success[600],
      light: success[500]
    },
    error: {
      main: error[600],
      light: error[500]
    },
    // ENG-747: the app's warning orange has always been the hand-rolled
    // `misc.warningText` (#e65100) at ~50 call sites, while `warning.main`
    // consumers silently got MUI's default #ed6c02 — two oranges for one meaning.
    // Defining `main` as the Suno one unifies them. `dark` must move off MUI's
    // #e65100 default (it would equal the new `main`, killing hover feedback on
    // warning-filled surfaces like the stale-build pill); #BF360C also clears
    // WCAG AA as text on white, which #e65100 (3.7:1) does not — see
    // features/sales-coach/CLAUDE.md. `light` is deliberately untouched: MUI
    // derives `<Alert severity="warning">` backgrounds from it, so every
    // existing warning Alert keeps its exact look.
    warning: {
      main: misc.warningText,
      dark: "#BF360C",
      contrastText: "#FFFFFF"
    },
    sideMenu: {
      main: "#2E1832",
      itemBgcolorSelected: "#C79AEB",
      itemBgcolor: "#FFFFFF",
      // Suno-staff rows (Administration) — amber, so staff can tell them from the practice's own.
      staffAccent: "#F2C879"
    },
    messages: {
      light: "#E3CCF5",
      dark: "#F1F1F1"
    },
    patientHeader: {
      main: "#F0F2FF",
      dark: "#F9B3B3"
    },
    patientSearch: {
      bgcolor: "rgba(199, 154, 235, 0.4)"
    },
    snackbar: {
      bgcolor: "#FFFFFF",
      textColor: "#000000"
    },
    chartNotes: {
      toolbar: {
        bgcolor: "rgba(217, 217, 217, 0.58)",
        textColor: "#000000"
      }
    },
    totalDisplay: {
      bgcolor: "rgba(34, 187, 51, 0.05)",
      color: "#22BB33"
    },
    avatar: {
      bgcolor: "rgba(0, 0, 0, 0.5)"
    },
    signature: {
      bgcolor: "rgba(0, 0, 0, 0.5)",
      emptyColor: "gray",
      changeColor: "#FFFFFF",
      iconColor: "#FFFFFF"
    },
    availabilitySlot: {
      bgcolor: "#3FC4BE",
      color: "#F9FAFF"
    },
    staffAvailabilityHeader: {
      bgcolor: "#BDBDBD",
      color: "#918B8B",
      headerColor: "#A7AFBC"
    },
    patientActiveProducts: {
      earTitleColor: "#C79AEB"
    },
    newHearingAids: {
      reviewItemColor: "#56657F",
      warrantyColor: "#7A807E",
      productsPriceTotalColor: "#56657F",
      wizardStepLinkColor: "#1F2937"
    },
    creditCardForm: {
      color: "#090A0A"
    },
    lineItemsTable: {
      emptySalesColor: "#7A807E"
    },
    invoiceInfoDisplay: {
      color: "#1F2937"
    },
    pureToneGraphSplittedView: {
      bgcolor: "#FFFFFF"
    },
    pureToneGraphMergedView: {
      bgcolor: "#FFFFFF"
    },
    customTest: {
      bgcolor: "#FFFFFF"
    },
    acousticReflexTable: {
      color: "#7A807E"
    },
    previewSpeechWordRecognition: {
      color: "#262727"
    },
    customStepIcon: {
      color: "#1B8281"
    },
    detailsSection: {
      color: "#A7AFBC",
      bgcolor: "#ECF9F8"
    },
    patientReviewDetail: {
      bgcolor: "unset",
      selectedBgcolor: "#F7F7F7",
      titleColor: "#7A807E"
    },
    patientReviewDetailSubGroup: {
      color: "#262727"
    },
    patientsQuickSearchTab: {
      selectedBgcolor: "#C79AEB",
      bgcolor: "#FFFFFF",
      selectedColor: "#FFFFFF",
      color: "unset",
      borderColor: "#C79AEB"
    },
    actionTab: {
      selectedBgcolor: "#C79AEB",
      bgcolor: "#FFFFFF",
      selectedColor: "#FFFFFF",
      color: "unset",
      borderColor: "#C79AEB"
    },
    actionBar: {
      bgcolor: "#F0F2FF"
    },
    patientsQuickSearchBar: {
      bgcolor: "#F0F2FF"
    },
    headerHearingDetail: {
      bgcolor: "#FFFFFF"
    },
    newPatientWizardActionButtons: {
      color: "#1F2937"
    },
    helpPage: {
      linkColor: "#1F2937"
    },
    timePicker: {
      bgcolor: "#FFFFFF"
    },
    lightBackground: "#F9FAFF",
    common: {
      black: "#000000",
      white: "#FFFFFF"
    },
    divider: neutral[300],
    text: {
      primary: neutral[900],
      secondary: neutral[800]
    }
  },
  shadows: {
    ...(Array(25).fill("none") as Shadows),
    ...sunoShadows
  },
  typography: {
    fontFamily: "LatoRegular, sans-serif",
    fontSize: sunoTheme.typography.pxToRem(16),
    fontWeightSemiBold: 600,
    fontWeightExtraBold: 700,
    h1: {
      fontFamily: "LatoRegular, sans-serif",
      fontWeight: 600,
      fontSize: sunoTheme.typography.pxToRem(36),
      lineHeight: sunoTheme.typography.pxToRem(42),
      letterSpacing: sunoTheme.typography.pxToRem(-0.32)
    },
    h2: {
      fontFamily: "LatoRegular, sans-serif",
      fontWeight: 600,
      fontSize: sunoTheme.typography.pxToRem(33),
      lineHeight: sunoTheme.typography.pxToRem(40),
      letterSpacing: sunoTheme.typography.pxToRem(-0.32)
    },
    h3: {
      fontFamily: "LatoRegular, sans-serif",
      fontWeight: 600,
      fontSize: sunoTheme.typography.pxToRem(28),
      lineHeight: sunoTheme.typography.pxToRem(34),
      letterSpacing: sunoTheme.typography.pxToRem(-0.32)
    },
    h4: {
      fontFamily: "LatoRegular, sans-serif",
      fontWeight: 600,
      fontSize: sunoTheme.typography.pxToRem(28),
      lineHeight: sunoTheme.typography.pxToRem(34),
      letterSpacing: sunoTheme.typography.pxToRem(-0.32)
    },
    h5: {
      fontFamily: "LatoRegular, sans-serif",
      fontWeight: 600,
      fontSize: sunoTheme.typography.pxToRem(24),
      lineHeight: sunoTheme.typography.pxToRem(30),
      letterSpacing: sunoTheme.typography.pxToRem(-0.32)
    },
    subtitle1: {
      fontFamily: "LatoRegular, sans-serif",
      fontSize: sunoTheme.typography.pxToRem(20),
      lineHeight: sunoTheme.typography.pxToRem(24)
    },
    body1: {
      fontFamily: "LatoRegular, sans-serif",
      fontSize: sunoTheme.typography.pxToRem(14),
      lineHeight: sunoTheme.typography.pxToRem(17),
      letterSpacing: 0
    },
    body2: {
      fontFamily: "LatoRegular, sans-serif",
      fontSize: sunoTheme.typography.pxToRem(16),
      lineHeight: sunoTheme.typography.pxToRem(22),
      letterSpacing: 0
    },
    body3: {
      fontFamily: "LatoRegular, sans-serif",
      fontSize: sunoTheme.typography.pxToRem(18),
      lineHeight: sunoTheme.typography.pxToRem(26),
      letterSpacing: 0
    },
    caption: {
      fontFamily: "LatoRegular, sans-serif",
      fontSize: sunoTheme.typography.pxToRem(12),
      lineHeight: sunoTheme.typography.pxToRem(16),
      letterSpacing: 0
    },
    button: {
      fontFamily: "LatoRegular, sans-serif",
      fontWeight: 700,
      fontSize: sunoTheme.typography.pxToRem(16),
      lineHeight: sunoTheme.typography.pxToRem(22),
      letterSpacing: 0
    }
  },
  components: getSunoComponents(sunoTheme)
});

export { sunoTheme };

declare module "@mui/material/styles" {
  interface TypographyVariants {
    body3: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    body3?: React.CSSProperties;
  }

  interface Palette {
    sideMenu: {
      main: string;
      itemBgcolorSelected: string;
      itemBgcolor: string;
      colorSelected: string;
      staffAccent: string;
    };
    patientHeader: Palette["primary"];
    neutral: ColorPartial;
    messages: Palette["primary"];
    patientSearch: {
      bgcolor: string;
    };
    chartNotes: {
      toolbar: {
        bgcolor: string;
        textColor: string;
      };
    };
    snackbar: {
      bgcolor: string;
      textColor: string;
    };
    totalDisplay: {
      bgcolor: string;
      color: string;
    };
    avatar: {
      bgcolor: string;
    };
    signature: {
      bgcolor: string;
      emptyColor: string;
      changeColor: string;
    };
    availabilitySlot: {
      bgcolor: string;
      color: string;
    };
    staffAvailabilityHeader: {
      bgcolor: string;
      color: string;
      headerColor: string;
    };
    patientActiveProducts: {
      earTitleColor: string;
    };
    newHearingAids: {
      reviewItemColor: string;
      warrantyColor: string;
      productsPriceTotalColor: string;
      wizardStepLinkColor: string;
    };
    creditCardForm: {
      color: string;
    };
    lineItemsTable: {
      emptySalesColor: string;
    };
    infoDisplay: {
      color: string;
    };
    invoiceInfoDisplay: {
      color: string;
    };
    pureToneGraphSplittedView: {
      bgcolor: string;
    };
    pureToneGraphMergedView: {
      bgcolor: string;
    };
    customTest: {
      bgcolor: string;
    };
    acousticReflexTable: {
      color: string;
    };
    previewSpeechWordRecognition: {
      color: string;
    };
    customStepIcon: {
      color: string;
    };
    detailsSection: {
      color: string;
      bgcolor: string;
    };
    patientReviewDetail: {
      bgcolor: string;
      selectedBgcolor: string;
      titleColor: string;
    };
    patientReviewDetailSubGroup: {
      color: string;
    };
    patientsQuickSearchTab: {
      selectedBgcolor: string;
      bgcolor: string;
      selectedColor: string;
      color: string;
      borderColor: string;
    };
    actionTab: {
      selectedBgcolor: string;
      bgcolor: string;
      selectedColor: string;
      color: string;
      borderColor: string;
    };
    actionBar: {
      bgcolor: string;
    };
    patientsQuickSearchBar: {
      bgcolor: string;
    };
    headerHearingDetail: {
      bgcolor: string;
    };
    newPatientWizardActionButtons: {
      color: string;
    };
    helpPage: {
      linkColor: string;
    };
    timePicker: {
      bgcolor: string;
    };
    lightBackground: string;
  }

  interface PaletteOptions extends Palette {}
}

declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    borderless: true;
    reportRun: true;
    reportDownload: true;
    sendAction: true;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    body3: true;
    subtitle2: false;
    h6: false;
  }
}
